<template>
  <SoDialog
    ref="payment-review-record-modal"
    v-model="isOpen"
    data-testid="ddr-modal"
    card-title="Details"
    class="ddr-record"
    :loading="recordQuery.isLoading"
  >
    <SoAlert v-if="hasError" type="error" data-testid="ddr-record-error">
      {{ recordQuery.error?.message || "Something went wrong" }}
    </SoAlert>

    <div v-else-if="recordQuery.isLoading" class="ddr-record__meterpoints">
      <SoSkeleton height="176px" />
      <SoSkeleton height="176px" />
    </div>

    <div
      v-else-if="recordQuery.data?.calculationItems?.length"
      class="ddr-record__meterpoints"
    >
      <SoWidget
        v-for="(item, index) in recordQuery.data?.calculationItems"
        :key="index"
      >
        <template #title>
          <div
            data-testid="ddr-calc-items-title"
            class="font-weight-medium mb-5"
          >
            {{ item.productType == "MPAN" ? "Electricity" : "Gas" }}
            ({{ item.productCode }})
          </div>
        </template>
        <SoRow class="mb-1">
          <SoCol>
            <div
              class="text-h4 mb-n1"
              data-testid="ddr-calc-items-monthly-cost"
            >
              {{
                !item.itemCostMonthly
                  ? undefined
                  : formatMoney({
                      amount: Number(item.itemCostMonthly.toFixed(2)),
                    })
              }}
            </div>
            <div>Monthly</div>
          </SoCol>
          <SoCol>
            <div class="text-h4 mb-n1" data-testid="ddr-calc-items-yearly-cost">
              {{
                !item.itemCost
                  ? undefined
                  : formatMoney({ amount: item.itemCost })
              }}
            </div>
            <div>Yearly</div>
          </SoCol>
        </SoRow>
        <div class="ml-n2">
          <SoExpansionPanels>
            <SoExpansionPanel>
              <template #title>
                <span
                  class="text-primary font-weight-light"
                  data-testid="ddr-calc-items-usage"
                >
                  {{
                    item.productType == "MPAN"
                      ? "Industry EAC " + item.meterpointEac1 + " kWh"
                      : "Industry AQ " + item.meterpointEac1 + " kWh"
                  }}
                </span>
              </template>
              <div>x {{ item.productRate1 }}p/kWh</div>
              <div>+ 365 x {{ item.productStandingRate }}p/day</div>
              <div>
                =
                {{
                  !item.itemCost
                    ? undefined
                    : formatMoney({ amount: item.itemCost })
                }}
              </div>
            </SoExpansionPanel>
          </SoExpansionPanels>
        </div>
      </SoWidget>
    </div>

    <SoAlert v-else type="info" data-testid="ddr-record-no-meterpoints">
      MPXN data not available
    </SoAlert>

    <template v-if="recordQuery.isLoading">
      <SoSkeleton class="mt-6 mb-7" height="2rem" />
      <SoSkeleton v-for="n in 6" :key="n" class="my-2" />
      <SoSkeleton class="my-3" height="2rem" />
    </template>

    <SoSummaryTable
      v-else
      title="Payment Calculation Details"
      :items="tableItems"
      :summary="tableSummary"
      collapsable
      open
    />
  </SoDialog>
</template>

<script lang="ts">
  import SoCol from "@/components/SoCol.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoRow from "@/components/SoRow.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoSummaryTable from "@/components/SoSummaryTable.vue"
  import SoExpansionPanel from "@/components/expansionPanels/SoExpansionPanel.vue"
  import SoExpansionPanels from "@/components/expansionPanels/SoExpansionPanels.vue"
  import { formatMoney } from "@/helpers/moneyHelpers"
  import { PaymentReviewService } from "@/services/operations/PaymentReviewService"
  import { defineComponent } from "vue"
  import {
    SoSummaryTableItem,
    SoSummaryTableValueType,
  } from "@/types/components/SoSummaryTable"

  export default defineComponent({
    components: {
      SoDialog,
      SoAlert,
      SoCol,
      SoRow,
      SoExpansionPanels,
      SoExpansionPanel,
      SoWidget,
      SoSkeleton,
      SoSummaryTable,
    },
    props: {
      modelValue: {
        type: Number,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        formatMoney,
        recordQuery: PaymentReviewService.getRecord(this.modelValue),
      }
    },
    computed: {
      isOpen: {
        get(): boolean {
          return !!this.modelValue
        },
        set(val: boolean) {
          if (!val) {
            this.$emit("update:modelValue", null)
          }
        },
      },
      tableItems(): SoSummaryTableItem[] {
        return [
          {
            type: SoSummaryTableValueType.DATE,
            title: "Next Payment Date",
            value: this.recordQuery.data?.nextPaymentDate,
          },
          {
            type: SoSummaryTableValueType.MONEY,
            title: "Estimated Annual Cost",
            value: this.recordQuery.data?.estimateAnnualCost,
            fallbackValue: "Not available",
          },
          {
            type: SoSummaryTableValueType.BALANCE,
            title: "Account Balance",
            value: this.recordQuery.data?.accountBalance,
            fallbackValue: "Not available",
          },
          {
            type: SoSummaryTableValueType.MONEY,
            title: "Account Balance Included in Calculation",
            value: this.recordQuery.data?.accountBalanceApplied,
            fallbackValue: "Not available",
          },
          {
            type: SoSummaryTableValueType.TEXT,
            title: "Total Cost",
            value: this.totalCostYearly,
            fallbackValue: "Not available",
          },
          {
            type: SoSummaryTableValueType.MONEY,
            title: "Current Monthly Payment",
            value: this.recordQuery.data?.paymentSchedulePeriodAmount,
            fallbackValue: "Not available",
            highlighted: true,
          },
        ]
      },
      tableSummary(): SoSummaryTableItem {
        return {
          type: SoSummaryTableValueType.MONEY,
          title: "Suggested Monthly Payment",
          value: this.recordQuery.data?.newMonthlyAmount,
          fallbackValue: "Not available",
        }
      },
      hasError() {
        return !!this.recordQuery.error
      },
      totalCostYearly() {
        if (
          this.recordQuery.data?.newMonthlyAmount === undefined ||
          !this.recordQuery.data.getTotalCost
        ) {
          return undefined
        }

        return (
          formatMoney({ amount: this.recordQuery.data.getTotalCost }) +
          "/yearly"
        )
      },
    },
  })
</script>

<style scoped>
  .ddr-record__meterpoints {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 1rem;
  }
</style>
